/**
 * Scroll History Manager for Dawn Theme
 * Preserves scroll position and page state when using browser back button
 * 
 * @version 1.0.0
 * @author OEM Parts Warehouse
 */

(function() {
  'use strict';

  const ScrollHistoryManager = {
    // Configuration
    config: {
      storageKey: 'oemPartsScrollHistory',
      scrollDebounceMs: 150,
      enableLogging: false, // Set to true for debugging
    },

    // State
    state: {
      currentPage: window.location.pathname + window.location.search,
      scrollPosition: 0,
      lastSaveTime: 0,
    },

    /**
     * Initialize the scroll history manager
     */
    init() {
      this.log('Initializing Scroll History Manager');
      
      // Only run on collection and search pages
      if (!this.isRelevantPage()) {
        this.log('Not a relevant page, skipping initialization');
        return;
      }

      // Restore scroll position if returning via back button
      this.restoreScrollPosition();

      // Set up event listeners
      this.bindEvents();
      
      this.log('Initialization complete');
    },

    /**
     * Check if current page should use scroll history
     */
    isRelevantPage() {
      const path = window.location.pathname;
      return path.includes('/collections/') || 
             path.includes('/search') ||
             path.includes('/pages/products-filter');
    },

    /**
     * Save current scroll position to sessionStorage
     */
    saveScrollPosition() {
      const now = Date.now();
      
      // Debounce saves
      if (now - this.state.lastSaveTime < this.config.scrollDebounceMs) {
        return;
      }

      this.state.lastSaveTime = now;
      this.state.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      const historyData = {
        url: this.state.currentPage,
        scrollY: this.state.scrollPosition,
        timestamp: now,
        filters: this.getActiveFilters(),
        sort: this.getActiveSortOrder(),
        page: this.getCurrentPage(),
        vfitzVehicle: this.getVFitzVehicleData(),
      };

      try {
        sessionStorage.setItem(this.config.storageKey, JSON.stringify(historyData));
        this.log('Saved scroll position:', historyData);
      } catch (e) {
        console.error('Failed to save scroll history:', e);
      }
    },

    /**
     * Restore scroll position from sessionStorage
     */
    restoreScrollPosition() {
      try {
        const savedData = sessionStorage.getItem(this.config.storageKey);
        
        if (!savedData) {
          this.log('No saved scroll history found');
          return;
        }

        const historyData = JSON.parse(savedData);
        
        // Check if we're returning to the same page
        if (historyData.url !== this.state.currentPage) {
          this.log('URL mismatch, not restoring scroll');
          return;
        }

        // Check if data is too old (> 30 minutes)
        const dataAge = Date.now() - historyData.timestamp;
        if (dataAge > 30 * 60 * 1000) {
          this.log('Saved data too old, clearing');
          sessionStorage.removeItem(this.config.storageKey);
          return;
        }

        // Restore scroll position
        this.log('Restoring scroll position:', historyData.scrollY);
        
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo({
            top: historyData.scrollY,
            left: 0,
            behavior: 'instant'  // No smooth scrolling for back button
          });
        });

        // Restore filter states if needed
        this.restoreFilterState(historyData);

      } catch (e) {
        console.error('Failed to restore scroll history:', e);
      }
    },

    /**
     * Get active filters from the page
     */
    getActiveFilters() {
      const filters = {};
      
      // Check for Dawn's facet system
      const facetInputs = document.querySelectorAll('.facets input[type="checkbox"]:checked, .facets input[type="radio"]:checked');
      facetInputs.forEach(input => {
        const filterName = input.name;
        if (!filters[filterName]) {
          filters[filterName] = [];
        }
        filters[filterName].push(input.value);
      });

      // Check for price range filter
      const priceMin = document.querySelector('.facets input[name="filter.v.price.gte"]');
      const priceMax = document.querySelector('.facets input[name="filter.v.price.lte"]');
      if (priceMin && priceMin.value) filters['price.min'] = priceMin.value;
      if (priceMax && priceMax.value) filters['price.max'] = priceMax.value;

      return filters;
    },

    /**
     * Get current sort order
     */
    getActiveSortOrder() {
      const sortSelect = document.querySelector('.facet-filters__sort select, select[name="sort_by"]');
      return sortSelect ? sortSelect.value : null;
    },

    /**
     * Get current pagination page number
     */
    getCurrentPage() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('page') || '1';
    },

    /**
     * Get VFitz vehicle selection data
     */
    getVFitzVehicleData() {
      // Check sessionStorage for VFitz data
      try {
        const vfitzData = sessionStorage.getItem('vfitz_vehicle_data');
        if (vfitzData) {
          return JSON.parse(vfitzData);
        }
      } catch (e) {
        this.log('No VFitz data found');
      }
      return null;
    },

    /**
     * Restore filter state
     */
    restoreFilterState(historyData) {
      // For now, filter restoration is passive
      // Active filters are maintained in URL parameters
      // This function can be expanded if custom filter restoration is needed
      this.log('Filter state preserved in URL parameters');
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Save scroll position periodically while scrolling
      let scrollTimeout;
      window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.saveScrollPosition();
        }, this.config.scrollDebounceMs);
      }, { passive: true });

      // Save before navigating away
      window.addEventListener('beforeunload', () => {
        this.saveScrollPosition();
      });

      // Save before clicking on product links
      document.addEventListener('click', (e) => {
        const productLink = e.target.closest('a[href*="/products/"]');
        if (productLink) {
          this.saveScrollPosition();
          this.log('Product link clicked, saved scroll position');
        }
      });

      // Clear storage on new search/filter (page reload)
      const facetForms = document.querySelectorAll('.facets-container form, .facet-filters__form');
      facetForms.forEach(form => {
        form.addEventListener('submit', () => {
          // Don't clear - let the new page URL be different so it doesn't restore
          this.log('Filter form submitted');
        });
      });

      // Handle pageshow event for bfcache (back-forward cache)
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          // Page was loaded from bfcache
          this.log('Page loaded from bfcache');
          this.restoreScrollPosition();
        }
      });

      // Handle popstate (back/forward buttons)
      window.addEventListener('popstate', () => {
        this.log('Popstate event - back button pressed');
        this.state.currentPage = window.location.pathname + window.location.search;
        this.restoreScrollPosition();
      });
    },

    /**
     * Logging helper
     */
    log(...args) {
      if (this.config.enableLogging) {
        console.log('[ScrollHistory]', ...args);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ScrollHistoryManager.init();
    });
  } else {
    ScrollHistoryManager.init();
  }

  // Expose to window for debugging
  window.ScrollHistoryManager = ScrollHistoryManager;

})();
